// @flow

import type { IAreBindable } from './interfaces/IAreBindable';
import type { IAreDestroyable } from './interfaces/IAreDestroyable';
import type { IHasElement } from './interfaces/IHasElement';
import type { ICanHasEvents } from './interfaces/ICanHasEvents';
import type { ICanNotify } from './interfaces/ICanNotify';
import EventObject from './events/EventObject';
import EventNames from './constants/EventNames';
import { throttle } from './functions/throttle';
import Notifications from '../constants/Notifications';
import { isVisible, toCamel, uppercaseFirst } from '../classes/Helpers';
import type { EventData } from '../classes/DataLayer/types';

class Notifiable implements IAreDestroyable, IAreBindable, IHasElement {
  #el: HTMLElement;
  name: string;

  /**
   *
   */
  get el(): HTMLElement {
    return this.#el;
  }

  /**
   *
   */
  bind(element: HTMLElement): void {
    this.#el = element;
  }

  /**
   * @abstract
   */
  destroy(): void {
  }
}

class Notification {
  #name: string;
  #target: Notifiable;
  #handler: function;

  /**
   *
   */
  constructor(pTarget: Notifiable, pName: string, pHandler: function) {
    this.#name = pName;
    this.#handler = pHandler;
    this.#target = pTarget;
  }

  /**
   *
   */
  get name(): string {
    return this.#name;
  }

  /**
   *
   */
  get handler(): function {
    return this.#handler;
  }

  /**
   *
   */
  get target(): Notifiable {
    return this.#target;
  }
}

class NotificationManager {
  // eslint-disable-next-line no-use-before-define
  static #instance: NotificationManager;

  #listeners: Array<Notification>;

  /**
   *
   */
  constructor() {
    if (NotificationManager.#instance) {
      throw new Error('Use Notification.getInstance()');
    }
    NotificationManager.#instance = this;
    this.#listeners = [];

    window.eventNotificationEnum.forEach((event: any) => {
      Livewire.on(event.name, (data: any) => {
        this.send(event.value, data, true);
      });
    });
  }

  /**
   *
   */
  get listeners(): Array<Notification> {
    return this.#listeners;
  }

  /**
   *
   */
  // eslint-disable-next-line no-use-before-define
  static getInstance(): NotificationManager {
    if (NotificationManager.#instance) {
      return NotificationManager.#instance;
    }
    return new NotificationManager();
  }

  /**
   * emit a notification
   *
   * @param pType
   * @param pParams
   * @param isLiveWireEvent
   */
  send(pType: string, pParams: Object = {}, isLiveWireEvent: boolean = false): void {
    const listeners: $ReadOnlyArray<Notification> = this.#listeners;
    const notes: $ReadOnlyArray<Notification> = listeners.filter((listener: Notification) => {
      return listener.name === pType;
    });

    if (!isLiveWireEvent) {
      Livewire.dispatch(pType, { data: pParams });
    }

    notes.forEach((note: Notification) => {
      pParams.notification = pType;
      note.handler(pParams);
    });
  }

  /**
   * Add a Notification Listener
   *
   * @param pTarget
   * @param pType
   * @param pHandler
   */
  addListener(pTarget: Notifiable, pType: string, pHandler: function): void {
    const note: Notification = new Notification(pTarget, pType, pHandler.bind(pTarget));
    this.#listeners.push(note);
  }

  /**
   * Remove a given listener. This only removes one record.
   *
   * @param pType - string Notification name
   * @param pTarget - Notifiable object
   */
  removeListener(pType: string, pTarget: Notifiable): void {
    const listeners: Array<Notification> = this.#listeners;
    const index: number = listeners.findIndex((notification: Notification): boolean => {
      return (notification.name === pType) && (notification.target === pTarget);
    });
    listeners.splice(index, 1);
  }

  /**
   *
   */
  remove(pInstance: Notifiable): void {
    const listeners: Array<Notification> = this.#listeners;
    this.#listeners = listeners.filter((notification: Notification): boolean => {
      return (notification.target !== pInstance);
    });
  }
}

class Component extends Notifiable implements ICanHasEvents, ICanNotify {
  #events: Array<EventObject>;
  #manager: NotificationManager;

  /**
   *
   */
  get manager(): NotificationManager {
    return this.#manager;
  }

  /**
   * Bind your component in the system.
   *
   * @param element - {HTMLElement} Connected element
   */
  bind(element: HTMLElement): void {
    super.bind(element);
    this.name = element.dataset.component;
    //
    this.#events = [];
    this.#manager = NotificationManager.getInstance();
  };

  /**
   *
   */
  loaded() {
    //
  }

  /**
   *
   */
  addEventListener(pEventName: string, pHandler: EventListener, options: Object = {}): void {
    this.#events.push(new EventObject(pEventName, pHandler));
    this.el.addEventListener(pEventName, pHandler, options);
  }

  /**
   *
   */
  removeEventListener(pEventName: string, pHandler: EventListener): void {
    const index: number = this.#events.findIndex((evtObj: EventObject) => {
      return (evtObj.name === pEventName) && (evtObj.handler === pHandler);
    });
    this.#events.splice(index, 1);
    this.el.removeEventListener(pEventName, pHandler);
  }

  /**
   *
   */
  addListener(pType: string, pHandler: function) {
    this.#manager.addListener(this, pType, pHandler);
  }

  /**
   *
   */
  removeListener(pType: string): void {
    this.#manager.removeListener(pType, this);
  }

  /**
   *
   */
  notify(pType: string, pParams: Object = {}) {
    this.#manager.send(pType, pParams);
  }

  /**
   * Destroy
   */
  destroy(): void {
    while (this.#events.length > 0) {
      this.removeEventListener(this.#events[0].name, this.#events[0].handler);
    }

    this.#manager.remove(this);
    super.destroy();
  }

  /**
   * Dispatches an event
   */
  dispatch(data: EventData): void {
    this.notify(Notifications.DISPATCH_EVENT, data);
  }
}

class App {
  // eslint-disable-next-line no-use-before-define
  static #instance: App;

  #body: HTMLBodyElement | null;
  #componentObserver: IntersectionObserver | null;
  components: Array<Component>;
  notifiables: Array<Notifiable>;

  /**
   *
   */
  constructor() {
    if (!App.#instance) {
      App.#instance = this;
    }
  }

  /**
   *
   */
  // eslint-disable-next-line no-use-before-define
  static getInstance(): App {
    if (App.#instance) {
      return App.#instance;
    }
    return new App();
  }

  /**
   *
   */
  init(): any {
    this.components = [];
    this.notifiables = [];

    document.addEventListener('DOMContentLoaded', () => {
      this.bind();
    }, false);

    window.onload = () => {
      this.load();
    };

    window.onbeforeunload = () => {
      this.destroy();
    };
  }

  /**
   *
   */
  bind(): void {
    this.#body = document.body;

    this.#componentObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries: Array<IntersectionObserverEntry>) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (!entry.isIntersecting || !isVisible(entry.target)) return;
          this.loadComponentsForElement(entry.target);
        });
      })
      : null;

    this.setComponents();

    const html: HTMLHtmlElement | null = document.querySelector('html');
    if (html) {
      html.classList.remove('no-js');
      html.classList.add('js');
    }

    const observer: MutationObserver = new MutationObserver((mutations: Array<MutationRecord>) => {
      mutations.forEach((mutation: MutationRecord) => {
        mutation.addedNodes.forEach((node: Node) => {
          if (!(node instanceof HTMLElement)) return;
          this.buildComponents(node);
        });

        mutation.removedNodes.forEach((node: Node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.hasAttribute('data-component')) {
            const component: ?Component = this.getComponentByElement(node);
            if (component) {
              this.destroyComponent(component);
            }
          }

          const componentElements: ?HTMLElement = node.querySelectorAll('[data-component]');
          if (!componentElements.length) return;

          componentElements.forEach((element: HTMLElement) => {
            const childComponent: ?Component = this.components.find((component: Component) => {
              return component.el === element;
            });
            if (!childComponent) return;

            this.destroyComponent(childComponent);
          });
        });
      });
    });

    observer.observe(document, { childList: true, subtree: true });
  }

  /**
   *
   */
  load(): void {
    this.components.forEach((pComponent: Component) => {
      pComponent.loaded();
    });
  }

  /**
   *
   */
  setComponents(): void {
    this.buildComponents(window.document);
  }

  /**
   *
   */
  buildComponents(element: HTMLElement): void {
    const componentElements: Array<HTMLElement> = [...element.querySelectorAll(['[data-component]'])];
    if (element instanceof HTMLElement && element.hasAttribute('data-component')) {
      componentElements.unshift(element);
    }

    componentElements.forEach((element: HTMLElement) => {
      if (this.getComponentByElement(element)) {
        console.warn('component is already instantiated for the given element');
        return;
      }

      if (element.dataset.lazy === undefined || !this.#componentObserver) {
        this.loadComponentsForElement(element);
        return;
      }

      this.#componentObserver.observe(element);
    });
  }

  /**
   * Load components for the given element
   */
  loadComponentsForElement(element: HTMLElement): void {
    let names: Array<string> = [];

    try {
      names = JSON.parse(element.dataset.component);
    } catch {
      names.push(element.dataset.component);
    }

    names.forEach((name: string) => this.loadComponent(name, element));
  }

  /**
   * Load a given component
   */
  loadComponent(name: string, element: HTMLElement): void {
    import(
      /* webpackExclude: /\.spec\.js|tests|__tests__|__mocks__|types|interfaces|external|constants|classes|stores|flow|abstract|jsti|critical-css/ */
      /* webpackPreload: true */
      '../components/' + uppercaseFirst(toCamel(name))
    )
      .then((response: { default: () => Component }) => {
        if (!response.default) {
          throw new Error(`Failed importing module: ${name}`);
        }

        const ComponentClass = response.default;
        const component = new ComponentClass();

        this.#componentObserver.unobserve(element);
        component.bind(element);

        this.components.push(component);
        this.notifiables.push(component);
      })
      .catch((error: Error) => console.warn(error));
  }

  /**
   *
   */
  destroy(): void {
    const self = this;
    while (self.notifiables.length > 0) {
      const notifiable: Notifiable = self.notifiables[0];
      if (notifiable) {
        notifiable.destroy();
      }
      self.notifiables.shift();
    }
    while (self.components.length > 0) {
      self.components.shift();
    }
  }

  /**
   * Get components by component name
   */
  getComponentsByName(name: string): Array<Component> {
    return this.components.filter((component: Component) => {
      return component.name === name;
    });
  }

  /**
   * Find an instance of a component by the given element
   */
  getComponentByElement(element: HTMLElement): ?Component {
    return this.components.find((component: Component) => {
      return component.el === element;
    });
  }

  /**
   * Destroy the given component and remove it from the list of active components
   */
  destroyComponent(component: Component): void {
    component.destroy();
    this.components.splice(this.components.indexOf(component), 1);
    this.notifiables.splice(this.notifiables.indexOf(component), 1);
  }
}

export {
  Notification,
  NotificationManager,
  Notifiable,
  Component,
  App,
  throttle
};
